import { FC, memo, useEffect, useMemo } from "react";
import { MatchStatus } from "service";
import { getTeamColors } from "components/colors";
import { Text } from "components/common/Text";
import { TwoTeamScoreBoard } from "../../../components/common";

type Pair = [number, number];

const ensurePair = (value: unknown): Pair => {
  if (Array.isArray(value) && value.length >= 2) {
    const a = Number(value[0]);
    const b = Number(value[1]);
    return [Number.isFinite(a) ? a : 0, Number.isFinite(b) ? b : 0];
  }

  if (typeof value === "string" && value.includes(":")) {
    const [homeRaw, awayRaw] = value.split(":");
    const home = Number(homeRaw);
    const away = Number(awayRaw);
    return [Number.isFinite(home) ? home : 0, Number.isFinite(away) ? away : 0];
  }

  return [0, 0];
};

const derivePointString = (value: unknown): string => {
  if (value === null || value === undefined) return "0";
  if (typeof value === "number") {
    if (Number.isFinite(value)) return String(value);
    return "0";
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "0";
    return trimmed.toUpperCase();
  }
  return "0";
};

export const Timer: FC<{ status: MatchStatus }> = memo(({ status }) => {
  const courtData = useMemo(
    () => ((status as any)?.c ?? {}) as Record<string, unknown>,
    [status]
  );
  const pc: number | undefined =
    typeof (status as any)?.pc === "number" ? (status as any).pc : undefined;

  const setEntries = useMemo(() => {
    const keys = ["S1", "S2", "S3", "S4", "S5"];
    return keys
      .map((key, index) => {
        const pair = ensurePair((courtData as any)?.[key]);
        const exists = pair[0] !== 0 || pair[1] !== 0;
        return {
          key,
          label: `S${index + 1}`,
          pair,
          exists,
        };
      })
      .filter((entry) => entry.exists);
  }, [courtData]);

  const totalSets = useMemo<Pair>(() => {
    const explicit = ensurePair((courtData as any)?.T);
    if (explicit[0] !== 0 || explicit[1] !== 0) return explicit;

    return setEntries.reduce<Pair>(
      (acc, { pair }) => {
        if (pair[0] > pair[1]) acc[0] += 1;
        else if (pair[1] > pair[0]) acc[1] += 1;
        return acc;
      },
      [0, 0]
    );
  }, [courtData, setEntries]);

  const pointsPair = useMemo<[string, string]>(() => {
    const rawPoints = (courtData as any)?.POINTS;
    if (Array.isArray(rawPoints)) {
      return [derivePointString(rawPoints[0]), derivePointString(rawPoints[1])];
    }
    if (typeof rawPoints === "string" && rawPoints.includes(":")) {
      const [homeRaw, awayRaw] = rawPoints.split(":");
      return [derivePointString(homeRaw), derivePointString(awayRaw)];
    }
    return ["0", "0"];
  }, [courtData]);

  const { home: homeColor, away: awayColor } = getTeamColors(status);

  useEffect(() => {
    const explicitCode = (status as any)?.nsc;
    const fallback = status.sc;

    let normalized = "";
    if (typeof explicitCode === "string" && explicitCode.length > 0) {
      normalized = explicitCode;
    } else if (typeof fallback === "string") {
      normalized = fallback.slice(-4);
    } else if (typeof fallback === "number") {
      normalized = String(fallback).slice(-4);
    }

    if (!normalized) return;
  }, [status]);

  return (
    <div className="absolute left-1/2 top-0 z-20 mt-[0.6vw] flex -translate-x-1/2 flex-col items-center gap-[0.4vw]">
      <TwoTeamScoreBoard
        width="11vw"
        gap="0.3vw"
        scoreHeight="4vw"
        accentHeight="1vw"
        homeAccentColor={homeColor}
        awayAccentColor={awayColor}
        homeScoreClassName="h-[4vw] flex items-center justify-center font-extrabold text-black bg-white"
        awayScoreClassName="h-[4vw] flex items-center justify-center font-extrabold text-black bg-white"
        homeContent={pointsPair[0]}
        awayContent={pointsPair[1]}
      />

      <div className="flex gap-[1vw] bg-[rgba(0,0,0,0.75)] px-[0.6vw] py-[0.4vw]">
        <div className="grid min-w-[2.8vw] grid-rows-2 gap-[0.2vw] text-center">
          <Text variant="tennisSets" className="font-bold text-statisticTitle">
            Total Sets
          </Text>
          <Text variant="tennisSets" className="font-extrabold text-white">
            {totalSets[0]}-{totalSets[1]}
          </Text>
        </div>
        {setEntries.map(({ key, label, pair }) => {
          const setIndex = Number(label.slice(1));
          const isActive = !!pc && setIndex === pc;
          return (
            <div
              key={key}
              className="grid min-w-[2.8vw] grid-rows-2 gap-[0.2vw] text-center"
            >
              <Text
                variant="tennisSets"
                className="font-bold uppercase text-statisticTitle"
              >
                {label}
              </Text>
              <Text
                variant="tennisSets"
                className={`font-extrabold text-white ${
                  isActive ? "text-yellow" : ""
                }`}
              >
                {pair[0]}-{pair[1]}
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
});
